import _ from "lodash";
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from "Auth/authConfig";
import { showModal } from "Store/general/auth";
import { store } from "Store/mainStore";
import { setTheme } from "Store/general/theme";
import { apiTimeout } from "Components/Misc/CaseDetails_System";
import { handleRestLogic } from "Components/Logics/systemLogics";

/** * Sets the request headers for Axios requests.
 * It retrieves the access token and id token from the Redux store,
 * and sets them in the request headers along with a content type.
 * * @param {Object} request - The Axios request object.
 * @return {Object} - The modified request object with headers set.
 */
export const setRequestHeaders = (request) => {
    // Get the access token and id token from the Redux store
    const reduxStore = store.getState();
    const accessToken = reduxStore?.authSlice?.login?.adToken?.accessToken;
    const idToken = reduxStore?.authSlice?.login?.adToken?.idToken;
    // If accessToken nd idToken are available, then upadte the request headers
    // If not, then we will not update the request headers and return the request as is
    if (accessToken && idToken) {
        request.headers["Content-Type"] = "application/json; charset=UTF-8";
        request.headers["Authorization"] = "Bearer " + accessToken;
        delete request.headers["idtoken"];
        request.headers["idtoken"] = idToken;
        request.timeout = 1000 * apiTimeout;
    }
    return request;
}

/**
 * Handles the response from Axios interceptors.
 * If the response contains a specific error code or message, it triggers appropriate dialogs.
 * 
 * @param {Object} response - The Axios response object.
 * @param {Function} dispatch - The dispatch function to trigger actions.
 * @returns {Object} - The processed response object.
 */
export const handleAxiosInterceptorsReponse = (response, dispatch) => {
    // Response interceptor to handle the response
    if (response.config?.["skipAlert"]) {
        return response;
    }
    if (response.data.errorCode === 401 &&
        (
            response.data.errorMessage.trim().toUpperCase().includes("TOKEN ERROR") ||
            response.data.errorMessage.trim().toUpperCase().includes("TOKEN EXPIRED")
        )
    ) {
        disptachTokenExpiredModal(dispatch, response.data.errorMessage.trim().toUpperCase());
    }
    else if (response.data.errorCode == 401) {
        showResponseDialog(dispatch, 401);
        // this.speak(401);
    }
    if (!response.data.status) {
        // this.speak(500, { msg: response.data.errorMessage, panel: { title: 'More details', msg: response.data.stacktrace } })
        showResponseDialog(dispatch, 500, { msg: response.data.errorMessage, panel: { title: 'More details', msg: response.data.stacktrace } });
    }
    return response;
}

/**
 * Dispatches a token expired modal.
 * 
 * @param {Function} dispatch - The dispatch function to trigger actions.
 * @param {String} title - The title of the modal.
 */
export const disptachTokenExpiredModal = (dispatch, title) => {
    dispatch(showModal({ msg: "Please log in again now...", variant: "ERROR", title, show: true }))
    // Show the message 5 second and redirect the page to login
    setTimeout(() => {
        const msalInstance = new PublicClientApplication(msalConfig);
        //@ts-ignore
        msalInstance.loginRedirect({
            redirectStartPage: process.env.REACT_APP_REDIRECT_URI
        });
    }, 3000);
}

/**
 * Shows a response dialog based on the response code.
 * 
 * @param {Function} dispatch - The dispatch function to trigger actions.
 * @param {Number} code - The response code.
 * @param {Object} info - The response info.
 */
export const showResponseDialog = (dispatch, code, info = { msg: '', panel: { title: '', msg: '' } }) => {
    switch (code) {
        case 401:
            dispatch(showModal({ msg: "Please Reload to continue , If you have any unsaved data, close this message and copy the data before logging in again. If not, please log in again now.", variant: "ERROR", title: "Session Timed Out", show: true }))
            break;
        case 403:
            dispatch(showModal({
                msg: info.msg, variant: "ERROR", title: "Something went wrong in response", show: true, panel: {
                    msg: info.panel.msg,
                    title: info.panel.title
                }
            }))
        case 200:
            break;
        case 500:
            dispatch(showModal({
                msg: info.msg, variant: "ERROR", title: "Something went wrong in response", show: true, panel: {
                    msg: info.panel.msg,
                    title: info.panel.title
                }
            }))
            break;
        case 400:
            dispatch(showModal({
                msg: info.msg, variant: "ERROR", title: info.panel?.title ? info.panel?.title : "Something went wrong in response", show: true, panel: {
                    msg: info.panel.msg,
                    title: info.panel.title
                }
            }))
            break;
    }
}

/**
 * Handles the case page reset logic.
 * It resets the theme and other components to their initial state.
 * 
 * @param {Function} dispatch - The dispatch function to trigger actions.
 */
export const handleCasePageReset = (dispatch) => {
    dispatch(
        setTheme({
            element: "select",
            comp: "subType",
            prop: "isOpen",
            value: null,
        })
    );
    dispatch(
        setTheme({
            element: "select",
            comp: "caseDetails_endCustomer",
            prop: "isDisabled",
            value: null,
        })
    );
    handleRestLogic();
}

/**
 * Retrieves the entire Redux store state, excluding the authentication slice, 
 * and returns it as a deep-cloned object for use in the UI.
 * 
 * @returns {Object} A deep-cloned object containing the Redux store state without the `authSlice`.
 */
export const getUIJsonState = (offer_id = undefined) => {
    const allStateData = _.cloneDeep(store.getState());
    delete allStateData['authSlice'];
    if (offer_id) {
        // add offer_id to the state dynamic slice
        allStateData['dynamic']['data']['offer_id'] = offer_id;
    }
    return allStateData;
}